/*  MUI  */
import { Error } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { Image } from 'application/Shared/Components';
import { RenderNoImage } from 'application/Shared/Components/RenderNoImage';
import {
    DarkTooltip,
    TooltipPlacement
} from 'application/Shared/Components/Tooltips';
import { TruncatedText } from 'application/Shared/Components/TruncatedText';
import { FontSizes } from 'application/Shared/Constants';
import { Route } from 'application/Shared/Helpers';

/*  React  */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

/*  Components  */
import Owner from './Application/Containers/Owner';

const getNameStyle = ({ textClassName, theme, useWhiteFont }) => {
    let style = {};

    if (useWhiteFont) {
        style.color = theme.palette.text.white;
    }
    if (textClassName?.includes('link')) {
        style = {
            ...style,
            '&.MuiTypography-span:hover': {
                color: theme.palette.hover.tertiary
            }
        };
        style.cursor = 'pointer';
    }
    if (textClassName?.includes('bold')) {
        style.fontWeight = 600;
    }
    if (textClassName?.includes('underline')) {
        style = {
            ...style,
            '&:hover': {
                textDecoration: 'underline',
                textDecorationColor: theme.palette.border.tertiary,
                textDecorationThickness: '1px',
                textUnderlineOffset: '3px'
            }
        };
    }
    if (textClassName?.includes('bigText')) {
        style.fontSize = '24px';
        style.lineHeight = '32px';
    }
    if (textClassName?.includes('colorSecondary')) {
        style.color = theme.palette.text.secondary;
    }

    return style;
};

const ApplicationContext = React.createContext({ application: null });

/**
 * Application component that provides context for application data and link behavior
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {Object} [props.data] - Application data object
 * @param {(string|Object)} [props.nameLinkTo] - Custom route to link to instead of default app page. Can be string path or object with pathname/search
 * @param {boolean} [props.nameHasLink] - If true, creates a link to the app page and sets className
 * @param {boolean} [props.rerender] - Flag to trigger re-render
 *
 * @returns {React.ReactElement} Application provider component
 */
const Application = ({ children, data, nameLinkTo, nameHasLink, rerender }) => {
    const [loaded, setLoaded] = useState(false);
    const [application, setApplication] = useState(data);

    useEffect(() => {
        if (data) {
            setApplication(data);
            setLoaded(true);
            return;
        }

        if (!data) {
            setApplication({ name: 'no_id_or_data' });
            setLoaded(true);
        }
    }, [data, rerender]);

    const app = useMemo(
        () => ({ application, nameHasLink, nameLinkTo }),
        [application, nameHasLink, nameLinkTo]
    );

    return (
        loaded && (
            <ApplicationContext.Provider value={app}>
                {children}
            </ApplicationContext.Provider>
        )
    );
};

const DiscoveredByBrowserHistoryBadge = () => {
    const { t } = useTranslation();

    return (
        <DarkTooltip
            arrow
            title={<div>{t('Tooltips.Badge.quickScanLogin')}</div>}
            placement="right"
        >
            <Error
                sx={{
                    color: 'icon.discoveredByQuickScan',
                    marginLeft: '8px',
                    marginRight: '4px'
                }}
                fontSize="small"
            />
        </DarkTooltip>
    );
};

const getTextClassName = ({
    applicationId,
    className,
    nameHasLink,
    nameLinkTo
}) => {
    if (nameLinkTo || (nameHasLink && applicationId)) {
        return `${className} link bold underline`;
    }
    if (nameHasLink && !applicationId) {
        return `bold ${className}`;
    }
    return className;
};

const RenderName = ({
    className,
    lines,
    name,
    noTruncate,
    onNameClick,
    scrollTo,
    showBrowserHistoryBadge,
    useWhiteFont
}) => {
    const theme = useTheme();
    const { application, nameHasLink, nameLinkTo } =
        useContext(ApplicationContext);
    const { id } = application;

    const scrollRef = `scroll-${id}`;

    let text = name;

    if (!name) {
        text = application.name;
    }

    text = text?.trim().slice(0, 50);

    const textClassName = getTextClassName({
        applicationId: application.id,
        className,
        nameHasLink,
        nameLinkTo
    });

    const nameStyle = getNameStyle({
        textClassName,
        theme,
        useWhiteFont
    });

    if (noTruncate || text.length <= 5) {
        return (
            <Box
                alignItems="center"
                data-testid="applicationRenderNameNoTruncate"
                display="flex"
                id={scrollTo && scrollRef}
                onClick={() => onNameClick()}
            >
                <RenderLink>
                    <Typography
                        variant="span"
                        id={scrollTo && scrollRef}
                        sx={{ ...nameStyle }}
                    >
                        {text}
                    </Typography>
                </RenderLink>
                {showBrowserHistoryBadge && <DiscoveredByBrowserHistoryBadge />}
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            id={scrollTo && scrollRef}
            data-testid="applicationRenderNameTruncateOutterWrapper"
        >
            <Box
                data-testid="applicationRenderNameTruncateWrapper"
                onClick={onNameClick}
                width="fit-content"
            >
                <RenderLink>
                    <TruncatedText
                        className={textClassName}
                        data-testid="applicationRenderNameTruncate"
                        component="span"
                        id={scrollTo && scrollRef}
                        containerClassName="link"
                        key={`application_name_${application.id}`}
                        maxLines={lines} // default is 1 if lines is undefined.
                        sx={{
                            ...(useWhiteFont ? { color: 'text.white' } : {}),
                            ...(textClassName?.includes('bold')
                                ? { fontWeight: 600 }
                                : {})
                        }}
                        text={text}
                        truncateText="…"
                        tooltipPlacement={TooltipPlacement.RIGHT}
                    />
                </RenderLink>
            </Box>
            {showBrowserHistoryBadge && <DiscoveredByBrowserHistoryBadge />}
        </Box>
    );
};

const RenderLink = ({ children }) => {
    const { application, nameHasLink, nameLinkTo } =
        useContext(ApplicationContext);
    const id = application?.id;

    const { clientId, partnerId } = useParams();
    const route = Route.buildBaseAdminRoute({ clientId, partnerId });

    const needsLink = nameLinkTo || (nameHasLink && id);

    if (needsLink) {
        const toParams = {};
        if (typeof nameLinkTo === 'object') {
            if (nameLinkTo.pathname) {
                toParams.pathname = nameLinkTo.pathname;
            }
            if (nameLinkTo.search) {
                toParams.search = nameLinkTo.search;
            }
        } else {
            toParams.pathname = clientId
                ? `${route}/application/${id}/details`
                : nameLinkTo;
        }

        return (
            <Link
                to={toParams}
                data-testid="applicationRenderLink"
                data-trackid="application_renderLink_routeTo"
            >
                {children}
            </Link>
        );
    }

    return children;
};

const RenderScrollFrom = ({
    handleScroll,
    className,
    name,
    lines,
    noTruncate,
    onNameClick,
    scrollTo,
    showBrowserHistoryBadge
}) => {
    const { application } = useContext(ApplicationContext);
    const { id } = application;
    const scrollClassName = 'link underline bold';

    return (
        <>
            <span
                className="no-print"
                onClick={() => handleScroll()}
                aria-hidden="true"
                data-testid="applicationRenderScrollFromNoPrint"
            >
                <RenderName
                    className={`${className} ${scrollClassName}`}
                    lines={lines}
                    name={name}
                    noTruncate={noTruncate}
                    onNameClick={onNameClick}
                    scrollTo={scrollTo}
                    showBrowserHistoryBadge={showBrowserHistoryBadge}
                />
            </span>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <a
                href={`#scroll-${id}`}
                className="print-only print-wrap showLink"
                data-testid="applicationRenderScrollFromPrintOnly"
                data-trackid="applicationRenderScrollFromPrintOnlyRenderName_scrollTo"
            >
                <RenderName
                    className={`${className} ${scrollClassName}`}
                    lines={lines}
                    name={name}
                    noTruncate={noTruncate}
                    onNameClick={onNameClick}
                    scrollTo={scrollTo}
                    showBrowserHistoryBadge={showBrowserHistoryBadge}
                    data-trackid="applicationRenderScrollFromPrintOnlyRenderName"
                />
            </a>
        </>
    );
};

const defaultFunction = () => {};

/**
 * Renders an application name with various display options and behaviors
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 * @param {number} [props.lines] - Number of lines to truncate text to
 * @param {string} [props.name] - Name to display, falls back to application.name from context
 * @param {boolean} [props.noTruncate] - If true, displays full text without truncation
 * @param {Function} [props.onNameClick] - Click handler for the name element
 * @param {boolean} [props.scrollFrom] - If true, enables scrolling from this element to target
 * @param {boolean} [props.scrollTo] - If true, makes this element a scroll target
 * @param {boolean} [props.showBrowserHistoryBadge] - If true, shows browser history badge
 * @param {boolean} [props.useWhiteFont] - If true, uses white text color
 * @returns {React.ReactElement} Rendered application name component
 */
const ApplicationName = ({
    className,
    lines,
    name,
    noTruncate,
    onNameClick = defaultFunction,
    scrollFrom,
    scrollTo,
    showBrowserHistoryBadge,
    useWhiteFont
}) => {
    const { application } = useContext(ApplicationContext);
    const { t } = useTranslation();

    if (!application) return null;

    const handleScroll = () => {
        const element = document.getElementById(`scroll-${application.id}`);
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth' });
    };

    if (typeof application.name === 'undefined' || application.name === null) {
        return (
            <span
                className={className}
                data-trackid="applicationNameClassAppNameNotFound"
            >
                {t('Application.unable_to_load_application_name')}
            </span>
        );
    }

    if (scrollFrom) {
        return (
            <RenderScrollFrom
                handleScroll={handleScroll}
                className={className}
                name={name}
                lines={lines}
                noTruncate={noTruncate}
                onNameClick={onNameClick}
                scrollTo={scrollTo}
                showBrowserHistoryBadge={showBrowserHistoryBadge}
                useWhiteFont={useWhiteFont}
            />
        );
    }

    return (
        <RenderName
            className={className}
            lines={lines}
            name={name}
            noTruncate={noTruncate}
            onNameClick={onNameClick}
            scrollTo={scrollTo}
            showBrowserHistoryBadge={showBrowserHistoryBadge}
            useWhiteFont={useWhiteFont}
        />
    );
};

/**
 * Renders an application description with truncation options
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 * @param {number} [props.lines] - Number of lines to truncate text to
 * @param {boolean} [props.noTruncate] - If true, displays full text without truncation
 * @returns {React.ReactElement} Rendered application description component
 */
const ApplicationDescription = ({ className, lines, noTruncate }) => {
    const { application } = useContext(ApplicationContext);
    const { description } = application;
    const text = description || 'N/A';

    if (noTruncate) {
        return text;
    }

    return (
        <TruncatedText
            className={className}
            component="div"
            key={`applicationDescription_${application.id}`}
            maxLines={lines}
            text={text}
        />
    );
};

/**
 * Renders the category of an application from context
 * If no category exists, displays "Category not found" translation
 *
 * @returns {string|null} Application category or translated "not found" message, null if no application in context
 */
const ApplicationCategory = () => {
    const { application } = useContext(ApplicationContext);
    const { t } = useTranslation();

    if (!application) return null;

    return application.category
        ? application.category
        : t('Common.categoryNotFound');
};

const getFontSize = (size) => {
    switch (size) {
        case 'smaller':
            return '9px';
        case 'normal':
            return FontSizes[1];
        case 'small':
        default:
            return FontSizes[0];
    }
};

/**
 * Renders an application's category and subcategory with display options
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 * @param {number} [props.lines] - Number of lines to truncate text to
 * @param {boolean} [props.noTruncate] - If true, displays full text without truncation
 * @param {('smaller'|'small'|'normal')} [props.size] - Font size variant to use
 * @param {boolean} [props.useWhiteFont] - If true, uses white text color
 * @returns {React.ReactElement} Rendered category and subcategory component
 */
const ApplicationCategoryAndSubCategory = ({
    className,
    lines,
    noTruncate,
    size,
    useWhiteFont
}) => {
    const theme = useTheme();
    const { application } = useContext(ApplicationContext);
    const { t } = useTranslation();

    const fontSize = getFontSize(size);

    if (!application) {
        return (
            <Typography
                data-testid="applicationCategoryAndSubCategoryNoApp"
                sx={{
                    color: useWhiteFont ? 'text.white' : 'text.primary',
                    fontSize
                }}
            >
                {t('Common.categoryNotFound')}
            </Typography>
        );
    }

    const { category, subCategory } = application;
    const subCat = subCategory ? ` / ${subCategory}` : null;

    let text = category ? `${category}${subCat}` : t('Common.categoryNotFound');

    text = text?.trim().slice(0, 50);

    if (noTruncate) {
        return (
            <Typography
                className={className}
                data-testid="applicationCategoryAndSubCategoryNoTruncate"
                sx={{
                    color: useWhiteFont ? 'text.white' : 'text.primary',
                    fontSize
                }}
            >
                {text}
            </Typography>
        );
    }

    return (
        <Box display="flex" alignItems="center" width="100%">
            <Box
                data-testid="applicationCategoryAndSubCategory"
                display="flex"
                width="fit-content"
            >
                <TruncatedText
                    className={className}
                    component="span"
                    key={`applicationCategoryAndSubCategory_${application.id}`}
                    maxLines={lines}
                    sx={{
                        color: useWhiteFont
                            ? theme.palette.text.white
                            : theme.palette.text.primary,
                        fontSize
                    }}
                    text={text}
                    tooltipPlacement={TooltipPlacement.RIGHT}
                />
            </Box>
        </Box>
    );
};

/**
 * Renders an application's image or a "no image" message
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the image
 * @param {boolean} [props.showTextForNoImage] - If true, displays "No Image" text when image is null
 * @param {Object} [props.style] - CSS styles to apply to the image or text
 * @returns {React.ReactElement} Rendered image or text component
 */
const ApplicationImage = ({ className, showTextForNoImage, style }) => {
    const { application } = useContext(ApplicationContext);
    const { t } = useTranslation();

    if (!application) return null;

    if (application.image === null && showTextForNoImage) {
        return <div style={style}>{t('Common.noImage')}</div>;
    }
    return (
        <Image
            className={className}
            style={style}
            size="16"
            imageId={application.image}
        />
    );
};

/**
 * Renders an application icon, either using a favicon or generating a styled text icon
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the icon container
 * @param {string} [props.classNameForNoImageInside] - CSS class name to apply to the inner text when no image exists
 * @param {string} [props.classNameForNoImageOutside] - CSS class name to apply to the outer container when no image exists
 * @param {string} [props.size] - Size of the icon
 * @param {Object} [props.sx] - MUI system props to apply custom styles
 * @returns {React.ReactElement} Rendered icon component
 */
const ApplicationIcon = ({
    className,
    classNameForNoImageInside,
    classNameForNoImageOutside,
    size = '16',
    sx
}) => {
    const { application } = useContext(ApplicationContext);

    if (!application) return null;

    const { name, favico } = application;

    if (!favico) {
        return (
            <RenderNoImage
                className={className}
                classNameForNoImageInside={classNameForNoImageInside}
                classNameForNoImageOutside={classNameForNoImageOutside}
                name={name}
                sx={sx}
            />
        );
    }

    return (
        <Image
            aria-label={name}
            className={className}
            imageId={favico}
            size={size}
            style={sx}
        />
    );
};

/**
 * Renders the creation date of an application from context
 * Returns the date in localized string format
 * Returns null if no application exists in context
 *
 * @returns {string|null} Localized date string or null
 */
const ApplicationDateCreated = () => {
    const { application } = useContext(ApplicationContext);

    if (!application) return null;

    return new Date(application.dateCreated).toLocaleDateString();
};

/**
 * Renders the base URL/name of an application from context
 * Returns null if no application exists in context or has no baseName
 *
 * @returns {string|null} Application base URL/name or null
 */
const ApplicationURL = () => {
    const { application } = useContext(ApplicationContext);

    return application?.baseName;
};

/* aliases */
/**
 * Renders the business owner information for an application
 * Wraps the Owner component with application context
 *
 * @param {Object} props - Props to pass through to the Owner component
 * @returns {React.ReactElement} Owner component with application from context
 */
const ApplicationBusinessOwner = (props) => {
    const { application } = useContext(ApplicationContext);
    return <Owner application={application} {...props} />;
};

export {
    Application,
    ApplicationCategoryAndSubCategory,
    ApplicationURL,
    ApplicationDateCreated,
    ApplicationName,
    ApplicationDescription,
    ApplicationIcon,
    ApplicationImage,
    ApplicationCategory,
    Owner,
    /* aliases */
    ApplicationBusinessOwner
};
